import CheckIcon from "@/assets/icons/Iconex/Light/CheckContainer.svg?react";
import CheckedIcon from "@/assets/icons/Iconex/Light/Checked.svg?react";
import {
	type PaletteMode,
	type Shadows,
	alpha,
	createTheme,
} from "@mui/material/styles";
import { COLORS_DARK, COLORS_LIGHT } from "./colors";

declare module "@mui/material/styles" {
	interface Palette {
		backBlue?: Palette["primary"];
	}
	interface PaletteOptions {
		backBlue?: PaletteOptions["primary"];
	}
}

// export const COLORS = {
// 	primary: "#316EE2",
// 	secondary: "#16D1D5",
// 	success: "#24BC6E",
// 	error: "#D44545",
// 	warning: "#E2BD07",
// 	disabled: "#C5C5C5",
// 	background: "#FFFFFF",
// 	backgroundBlue: "#ECFAFF",
// 	backgroundGray: "#EFEFEF",
// 	grayLight: "#DCDCDC",
// 	gray: "#454545",
// 	gray2: "#5D5D5D",
// 	gray3: "#6A6A6A",
// 	gray4: "#BBBBBB",
// 	divider: "#EFEFEF",
// 	black: "#000000",
// 	white: "#FFFFFF",
// };

export const shadows = {
	base: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
	drop: "0 0 22.7px 0 rgba(0, 0, 0, 0.15)",
};

export const buildTheme = (mode: PaletteMode) => {
	const COLORS = mode === "light" ? COLORS_LIGHT : COLORS_DARK;
	return createTheme({
		cssVariables: true,
		palette: {
			mode,
			backBlue: {
				main: COLORS.backgroundBlue,
			},
			background: {
				default: COLORS.backgroundGray,
				paper: COLORS.background,
			},
			text: {
				primary: COLORS.gray,
				secondary: COLORS.gray2,
				disabled: COLORS.disabled,
			},
			primary: {
				main: COLORS.primary,
				light: alpha(COLORS.primary, 0.2),
			},
			secondary: {
				main: COLORS.secondary,
				light: alpha(COLORS.secondary, 0.2),
			},
			success: {
				main: COLORS.success,
				light: alpha(COLORS.success, 0.2),
			},
			error: {
				main: COLORS.error,
				light: alpha(COLORS.error, 0.2),
			},
			warning: {
				main: COLORS.warning,
				light: alpha(COLORS.warning, 0.2),
			},
			divider: COLORS.divider,
			grey: {
				"50": COLORS.grayLight,
				"100": COLORS.gray,
				"200": COLORS.gray2,
				"300": COLORS.gray3,
				"400": COLORS.gray4,
			},
		},
		shadows: [
			"none",
			shadows.base,
			shadows.drop,
			...new Array<string>(22).fill("none"),
		] as Shadows,
		typography: {
			fontFamily: ["Inter", "faIR", "sans-serif"].join(","),
			h1: {
				fontWeight: 600,
				fontSize: "4.5rem",
				lineHeight: "auto",
			},
			h2: {
				fontWeight: 600,
				fontSize: "3.5rem",
				lineHeight: "auto",
			},
			h3: {
				fontWeight: 600,
				fontSize: "3rem",
				lineHeight: "auto",
			},
			h4: {
				fontWeight: 600,
				fontSize: "2rem",
				lineHeight: "auto",
			},
			h5: {
				fontWeight: 600,
				fontSize: "1.5rem",
				lineHeight: "auto",
			},
			h6: {
				fontWeight: 600,
				fontSize: "1.375rem",
				lineHeight: "auto",
			},
			subtitle1: {
				fontWeight: 700,
				fontSize: "1.125rem",
				lineHeight: "auto",
			},
			subtitle2: {
				fontWeight: 600,
				fontSize: "1.125rem",
				lineHeight: "auto",
			},
			body1: {
				fontWeight: 600,
				fontSize: "1rem",
				lineHeight: "auto",
			},
			body2: {
				fontWeight: 500,
				fontSize: "1rem",
				lineHeight: "auto",
			},
			body3: {
				fontWeight: 400,
				fontSize: "1rem",
				lineHeight: "auto",
			},
			caption: {
				fontWeight: 500,
				fontSize: "0.75rem",
				lineHeight: "1.125rem",
				letterSpacing: "0.015rem",
			},
			button: {
				fontWeight: 700,
				fontSize: "0.875rem",
				lineHeight: "auto",
			},
		},
		spacing: (factor: number) => `${0.25 * factor}rem`,
		components: {
			MuiIconButton: {
				styleOverrides: {
					root: {
						borderRadius: "50%",
						padding: 3,
						boxShadow: shadows.base,
						"&.Mui-disabled": {
							backgroundColor: COLORS.disabled,
							color: COLORS.background,
						},
						"&:hover": {
							backgroundColor: COLORS.background,
							boxShadow: shadows.drop,
						},
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						borderRadius: "8px",
						"&.Mui-disabled": {
							backgroundColor: COLORS.disabled,
							color: COLORS.white,
						},
						"&:hover": {
							boxShadow: shadows.drop,
						},
					},
					sizeSmall: {
						padding: "5.5px 19px",
					},
					sizeMedium: {
						padding: "10px 19px",
					},
					sizeLarge: ({ theme }) => ({
						...theme.typography.button,
						padding: "12.7px 19px",
					}),
				},
				variants: [
					{
						props: { variant: "outlined" },
						style: {
							borderWidth: "1px",
							borderColor: "currentColor",
							":hover": {
								borderWidth: "1px",
							},
							"&.Mui-disabled": {
								backgroundColor: "transparent",
								color: COLORS.disabled,
								borderWidth: "2px",
							},
						},
					},
					{
						props: { variant: "text", color: "primary" },
						style: {
							backgroundColor: COLORS.backgroundBlue,
							":hover": {
								backgroundColor: COLORS.backgroundBlue,
							},
						},
					},
				],
				defaultProps: {
					variant: "contained",
					disableElevation: true,
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: "8px",
						"& input:-webkit-autofill": {
							WebkitBoxShadow: `0 0 0 100px ${COLORS.input} inset`,
							WebkitTextFillColor: COLORS.gray,
							caretColor: COLORS.gray,
						},
						"& input:-webkit-autofill:focus": {
							WebkitBoxShadow: `0 0 0 100px ${COLORS.input} inset`,
						},
					},
				},
			},
			MuiInputBase: {
				styleOverrides: {
					root: {
						fontSize: "1rem",
						fontWeight: 500,
						"&.MuiInputBase-root .MuiInputBase-input.MuiInputBase-inputSizeSmall":
							{
								padding: "8.5px 16px !important",
							},
						"&.MuiInputBase-root .MuiInputBase-input": {
							padding: "11.5px 16px !important",
						},
						background: COLORS.input,
						borderColor: COLORS.backgroundGray,
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						fontSize: "1rem",
						fontWeight: 500,
						"&.MuiInputLabel-outlined": {
							transform: "translate(16px, 13.5px) scale(1)",
						},
						"&.MuiInputLabel-outlined.MuiInputLabel-shrink": {
							transform: "translate(14px, -8.5px) scale(0.75)",
						},
						"&.MuiInputLabel-sizeSmall.MuiInputLabel-outlined": {
							transform: "translate(16px, 8.5px) scale(1)",
						},
						"&.MuiInputLabel-sizeSmall.MuiInputLabel-outlined.MuiInputLabel-shrink":
							{
								transform: "translate(14px, -8.5px) scale(0.75)",
							},
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						boxShadow: shadows.drop,
						border: "none",
						borderRadius: "8px",
					},
				},
			},
			MuiLink: {
				styleOverrides: {
					root: {
						textDecoration: "none",
						"&:hover": { textDecoration: "underline" },
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						borderRadius: "14px",
						padding: "16px",
						marginTop: "12px",
						boxShadow: shadows.base,
						backgroundImage: "none",
					},
				},
			},
			MuiList: {
				styleOverrides: {
					root: {
						gap: "8px",
						display: "flex",
						flexDirection: "column",
					},
				},
			},
			MuiMenu: {
				defaultProps: {
					transformOrigin: { horizontal: "center", vertical: "top" },
					anchorOrigin: { horizontal: "center", vertical: "bottom" },
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						borderRadius: "8px",
						fontWeight: 500,
						fontSize: "0.75rem",
						lineHeight: "1.125rem",
						"&:focus": {
							backgroundColor: COLORS.background,
						},
						"&:hover": {
							backgroundColor: alpha(COLORS.primary, 0.2),
							color: COLORS.primary,
						},
						"&.active": {
							backgroundColor: alpha(COLORS.primary, 0.2),
							color: COLORS.primary,
						},
						"&.Mui-selected": {
							backgroundColor: alpha(COLORS.primary, 0.2),
							color: COLORS.primary,
						},
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					inputRoot: {
						paddingBlock: 0,
						paddingInlineStart: 0,
						"& .MuiIconButton-root": {
							boxShadow: "none",
						},
					},

					paper: {
						maxHeight: "360px",
					},

					listbox: {
						gap: "8px",
						display: "flex",
						flexDirection: "column",
						overflow: "visible",
					},

					option: ({ theme }) => ({
						...theme.typography.body2,
						borderRadius: "8px",
						minHeight: "30px !important",
						"&:focus": {
							backgroundColor: COLORS.background,
						},
						"&:hover": {
							backgroundColor: `${alpha(COLORS.primary, 0.2)} !important`,
							color: COLORS.primary,
						},
						"&[aria-selected='true']": {
							backgroundColor: alpha(COLORS.primary, 0.2),
							color: COLORS.primary,
						},
					}),
				},
				defaultProps: {
					slotProps: {
						chip: {
							size: "small",
						},
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: ({ theme }) => ({
						...theme.typography.subtitle1,
						textTransform: "none",
						color: COLORS.gray3,
					}),
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: COLORS.background,
						color: COLORS.gray2,
						boxShadow: shadows.base,
					},
					arrow: {
						color: COLORS.background,
					},
				},
			},
			MuiCheckbox: {
				defaultProps: {
					icon: <CheckIcon />,
					checkedIcon: <CheckedIcon />,
				},
			},
			MuiFormControlLabel: {
				styleOverrides: {
					root: {
						color: COLORS.gray3,
					},
				},
				defaultProps: {
					slotProps: {
						typography: { variant: "body2" },
					},
				},
			},
		},
	});
};

// export default marketTheme;
